<script setup lang="ts">
import { LucideChevronLeft, LucideChevronRight } from 'lucide-vue-next'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, Navigation } from 'swiper/modules'
import { EntityNewsCard } from '~/components/entity/news'
import type { INews } from '~/components/entity/news'
import type { IResponseData } from '~/types'
import type { IProps } from './types'

import './style.css'

const props = withDefaults(defineProps<IProps>(), {
  path: '/news',
  variant: 'light',
  hideImage: false,
  slidesPerViewMd: 3,
  slidesPerViewSm: 2,
  hideAddToFavorites: false
})

const url = computed(() => useUrlWithParams(props.path, props.queryParams))

const { data } = useApi<IResponseData<INews[]>>(() => url.value, {
  key: `news-page.${url.value}`
})

const id = useId()
const news = computed(() => data.value?.data)
</script>

<template>
  <section
    v-if="data && data.data.length"
    class="-mx-4 space-y-6 px-4 py-6 md:mx-0 md:rounded-xl md:px-6"
    :class="props.variant === 'dark' ? 'bg-brand-gray text-white' : 'bg-white'"
  >
    <header class="flex flex-wrap gap-4">
      <Heading tag="h2" class="mr-auto">{{ props.heading }}</Heading>
      <div class="flex gap-6">
        <button :class="`news-slider-${id}-prev`" :aria-label="$t('common.scroll_left')">
          <LucideChevronLeft />
        </button>
        <button :class="`news-slider-${id}-next`" :aria-label="$t('common.scroll_right')">
          <LucideChevronRight />
        </button>
      </div>
    </header>
    <Swiper
      class="widget-news-slider"
      :modules="[Autoplay, Navigation]"
      :autoplay="{ delay: 2500, pauseOnMouseEnter: true }"
      :navigation="{ prevEl: `.news-slider-${id}-prev`, nextEl: `.news-slider-${id}-next` }"
      space-between="8"
      slides-per-view="auto"
      :initial-slide="1"
      :centered-slides="true"
      loop
      :breakpoints="{
        768: {
          spaceBetween: 12,
          centeredSlides: false,
          slidesPerView: slidesPerViewSm || 2
        },
        1280: {
          spaceBetween: 16,
          centeredSlides: false,
          slidesPerView: props.slidesPerViewMd || 3
        }
      }"
    >
      <SwiperSlide v-for="slide in news" :key="slide.id" class="p-1">
        <EntityNewsCard
          v-bind="{
            ...slide,
            picture: hideImage ? '' : slide.picture,
            variant,
            hideAddToFavorites
          }"
        />
      </SwiperSlide>
    </Swiper>
  </section>
</template>
